import React, { useEffect, useMemo, useState } from 'react'
import { Table } from 'react-bootstrap'
import { Box, List, ListItem, Typography } from '@mui/material'

import ClientHeader from '../../../../components/ClientHeader/ClientHeader'
import GuidanceNote from '../../../../components/GuidanceNote'
import SearchInput from '../../../../components/SearchInput'
import Pagination from '../../../../layout/Pagination'
import { canManagePocStoreRelations } from '../../../../global_context/capabilities/pocCapabilities'
import {
  STORE_RELATIONS_FALLBACK_TEXT,
  STORE_RELATIONS_NO_RESULTS_TEXT
} from '../../constants/storeConstants'
import GoogleIcon from '../../../../icon/GoogleIcon'
import { requiresStoreRelationPrincipalReassignment } from '../../helpers/storeRelationAssignmentUtils'
import StoreRelationBrandsCell from '../components/StoreRelationBrandsCell'
import {
  STORE_DETAILS_FIELDS,
  STORE_HEADER_CONFIG,
  STORE_RELATION_SHOW_BRANDS_GUIDANCE_TITLE,
  STORE_RELATION_SHOW_BRANDS_NOTE,
  STORE_RELATION_SHOW_BRANDS_RULES,
  STORE_RELATION_SHOW_BRANDS_RULES_TITLE,
  STORE_RELATION_SHOW_BRANDS_TABLE_COLUMNS,
  STORE_RELATION_SHOW_DETAILS_TITLE,
  STORE_RELATION_SHOW_EDIT_BUTTON_LABEL,
  STORE_RELATION_SHOW_PAGE_SIZE,
  STORE_RELATION_SHOW_PENDING_PRINCIPAL_COLOR,
  STORE_RELATION_SHOW_USERS_SEARCH_PLACEHOLDER
} from '../constants/storeShowConstants'
import {
  buildStoreRelationShowDetailsData,
  buildStoreRelationShowUserRows,
  filterStoreRelationShowUserRows
} from '../utils/storeShowUtils'

const StoreDetailsTab = ({ data, onEdit, disableEditActions = false }) => {
  const [searchInput, setSearchInput] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const canWrite = canManagePocStoreRelations()
  const isPrincipalSuspended = data?.client_principal?.status === 'suspended'
  const hasPendingPrincipalReassignment = requiresStoreRelationPrincipalReassignment(data)
  const shouldShowAssignedBrandsSection = Boolean(data?.flags?.split_order_by_brand)
  const areEditActionsDisabled = disableEditActions || isPrincipalSuspended || !canWrite

  const userRows = useMemo(() => buildStoreRelationShowUserRows(data), [data])
  const detailsData = useMemo(
    () => buildStoreRelationShowDetailsData(data, userRows),
    [data, userRows]
  )

  const filteredRows = useMemo(() => {
    return filterStoreRelationShowUserRows(userRows, searchInput)
  }, [searchInput, userRows])

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / STORE_RELATION_SHOW_PAGE_SIZE))

  useEffect(() => {
    setCurrentPage(1)
  }, [searchInput])

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages)
    }
  }, [currentPage, totalPages])

  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * STORE_RELATION_SHOW_PAGE_SIZE
    return filteredRows.slice(start, start + STORE_RELATION_SHOW_PAGE_SIZE)
  }, [currentPage, filteredRows])

  const handleSearchChange = (event) => {
    setSearchInput(event.target.value)
  }

  const handleClearSearch = () => {
    setSearchInput('')
  }

  const handlePaginationClick = (event) => {
    event.preventDefault()
    let target = event.target

    if (target.tagName !== 'A') {
      target = target.closest('a')
    }

    if (!target?.id) return

    switch (target.id) {
      case 'first-page':
        setCurrentPage(1)
        break
      case 'last-page':
        setCurrentPage(totalPages)
        break
      case 'previous-page':
        setCurrentPage((prev) => Math.max(1, prev - 1))
        break
      case 'next-page':
        setCurrentPage((prev) => Math.min(totalPages, prev + 1))
        break
      default:
        break
    }
  }

  return (
    <div className="container-fluid">
      <div className="store-relations-details-section-header">
        <h3 className="margin-text-subtitle">{STORE_RELATION_SHOW_DETAILS_TITLE}</h3>

        {typeof onEdit === 'function' && (
          <button
            type="button"
            className="store-relations-details-btn store-relations-details-btn--primary"
            onClick={onEdit}
            disabled={areEditActionsDisabled}
          >
            <GoogleIcon
              name="edit"
              size={16}
              filled={false}
              className="store-relations-details-btn__icon"
            />
            {STORE_RELATION_SHOW_EDIT_BUTTON_LABEL}
          </button>
        )}
      </div>

      <hr />

      <ClientHeader data={detailsData} config={STORE_HEADER_CONFIG} />

      <hr />

      <Box display="flex" flexDirection="column" gap={2} sx={{ py: 1 }}>
        {STORE_DETAILS_FIELDS.map((field) => (
          <Box
            key={field.valueKey}
            display="flex"
            alignItems="flex-start"
            sx={{
              columnGap: 2,
              rowGap: 1,
              flexWrap: {
                xs: 'wrap',
                md: 'nowrap'
              }
            }}
          >
            <Box
              component="strong"
              sx={{
                minWidth: {
                  xs: '100%',
                  md: 200
                },
                fontSize: '1rem',
                lineHeight: 1.5
              }}
            >
              {field.label}
            </Box>

            <Box
              sx={{
                fontSize: '1rem',
                lineHeight: 1.5,
                flex: 1,
                color:
                  field.valueKey === 'principal_salesman_name' && hasPendingPrincipalReassignment
                    ? STORE_RELATION_SHOW_PENDING_PRINCIPAL_COLOR
                    : 'inherit',
                fontWeight:
                  field.valueKey === 'principal_salesman_name' && hasPendingPrincipalReassignment
                    ? 700
                    : 400
              }}
            >
              {detailsData[field.valueKey] ?? STORE_RELATIONS_FALLBACK_TEXT}
            </Box>
          </Box>
        ))}
      </Box>

      {shouldShowAssignedBrandsSection && (
        <>
          <hr />

          <div className="store-relations-users-section">
            <GuidanceNote
              title={STORE_RELATION_SHOW_BRANDS_GUIDANCE_TITLE}
              message={STORE_RELATION_SHOW_BRANDS_NOTE}
              sx={{ mb: 2 }}
              messageSx={{ mb: 1 }}
            >
              <Typography
                component="h5"
                sx={{
                  color: '#374151',
                  fontSize: '0.9rem',
                  fontWeight: 700,
                  lineHeight: 1.5,
                  mb: 0.5
                }}
              >
                {STORE_RELATION_SHOW_BRANDS_RULES_TITLE}
              </Typography>

              <List
                disablePadding
                sx={{
                  listStyleType: 'disc',
                  pl: 2,
                  color: '#4B5563',
                  '& .MuiListItem-root': {
                    display: 'list-item',
                    py: 0,
                    pl: 0,
                    fontSize: '0.80rem',
                    lineHeight: 1.5,
                    ml: 1
                  }
                }}
              >
                {STORE_RELATION_SHOW_BRANDS_RULES.map((rule) => (
                  <ListItem key={rule}>
                    <Typography
                      component="span"
                      sx={{
                        color: 'inherit',
                        fontSize: '0.80rem',
                        lineHeight: 1.5,
                        fontWeight: 500
                      }}
                    >
                      {rule}
                    </Typography>
                  </ListItem>
                ))}
              </List>
            </GuidanceNote>

            <div className="store-relations-users-search">
              <SearchInput
                value={searchInput}
                onChange={handleSearchChange}
                onClear={handleClearSearch}
                placeholder={STORE_RELATION_SHOW_USERS_SEARCH_PLACEHOLDER}
              />
            </div>

            <Table hover responsive className="oc-fixed-table">
              <thead className="table-header-gray-poc">
                <tr>
                  {STORE_RELATION_SHOW_BRANDS_TABLE_COLUMNS.map((column) => (
                    <th key={column.key} className={column.className}>
                      {column.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedRows.length === 0 ? (
                  <tr>
                    <td colSpan={STORE_RELATION_SHOW_BRANDS_TABLE_COLUMNS.length}>
                      {STORE_RELATIONS_NO_RESULTS_TEXT}
                    </td>
                  </tr>
                ) : (
                  paginatedRows.map((row) => (
                    <tr
                      key={row.key}
                      className={row.allBrandsInError ? 'store-relations-user-row--error-soft' : ''}
                    >
                      <td className="oc-td-text store-relations-user-cell">{row.userLabel}</td>
                      <td className="oc-td-text store-relations-brands-cell">
                        <StoreRelationBrandsCell rowKey={row.key} brands={row.brands} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>

            {totalPages > 1 && (
              <nav className="index-table store-relations-details-pagination" onClick={handlePaginationClick}>
                <Pagination current_page={currentPage} total_pages={totalPages} />
              </nav>
            )}
          </div>
        </>
      )}
    </div>
  )
}

export default StoreDetailsTab
